<template>
  <UForm id="create-user" :schema="schema" :state="state" @submit="onSubmit">
    <UPageCard variant="subtle" class="mt-4">
      <div class="flex flex-col gap-4">
        <UFormField name="email" label="Email (username)" required>
          <UInput v-model="state.email" type="email" class="w-full" />
        </UFormField>
        
        <UFormField name="firstName" label="First name" required>
          <UInput v-model="state.firstName" class="w-full" />
        </UFormField>
        
        <UFormField name="lastName" label="Last name" required>
          <UInput v-model="state.lastName" class="w-full" />
        </UFormField>
        
        <UFormField name="employerBpIds" label="Employer" required>
          <USelectMenu
            v-model="selectedEmployer"
            v-model:search-term="employerSearchTerm"
            :items="filteredEmployers"
            :loading="employersLoading"
            value-key="bpId"
            label-key="label"
            ignore-filter
            class="w-full"
            placeholder="Search employer..."
          />
        </UFormField>
        
        <SuperUserOrgPicker
          v-if="isSupplierSuperUser"
          v-model="state.clientId"
          :organizations="organizations ?? []"
          :organizations-loading="organizationsLoading ?? false"
        />
        <AdminOrgPicker
          v-else
          v-model="state.clientId"
          :organizations="organizations ?? []"
          :organizations-loading="organizationsLoading ?? false"
          @search-organizations="emit('searchOrganizations', $event)"
        />
        
        <UFormField name="groups" label="Role" required>
          <template v-if="singleSupplierRole">
            <p class="text-sm text-muted">
              Auto-assigned: {{ roleDisplayName(singleSupplierRole.path) }}
            </p>
          </template>
          <USelectMenu
            v-else-if="showRoleDropdown"
            v-model="state.groups"
            :items="groupItems"
            value-key="path"
            label-key="label"
            multiple
            class="w-full"
            :loading="groupsLoading"
          />
        </UFormField>
        
        <UFormField name="sapUserId" label="SAP User ID" :required="isDriverSelected">
          <UInput v-model="state.sapUserId" :placeholder="isDriverSelected ? 'Required for driver role' : 'Optional'" class="w-full" />
        </UFormField>
        
        <div class="flex justify-end mt-3">
          <UButton form="create-user" label="Create new user" type="submit" :disabled="working" :loading="working" />
        </div>
      </div>
    </UPageCard>
  </UForm>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { z } from 'zod';
import Fuse from 'fuse.js';
import type { FormSubmitEvent } from '@nuxt/ui';
import { api, CreateUserDto, EmployerPartnerDto, GroupDto, OrganizationDto } from '@/api/eOrdersApi';
import { useApiAdapter } from '@/store';
import { showError } from '@/utils/utils';
import SuperUserOrgPicker from '@/components/users/SuperUserOrgPicker.vue';
import AdminOrgPicker from '@/components/users/AdminOrgPicker.vue';

const props = defineProps<{
  organizations?: OrganizationDto[];
  employers?: EmployerPartnerDto[];
  groups?: GroupDto[];
  isSupplierSuperUser: boolean;
  supplierRoles: string[];
  organizationsLoading?: boolean;
  employersLoading?: boolean;
  groupsLoading?: boolean;
}>();

const toast = useToast();
const apiAdapter = useApiAdapter();

const DRIVER_ROLE = 'driver';

const hasDriverRole = (groups: string[] | undefined) => groups?.some(g => g.split('/').pop() === DRIVER_ROLE) ?? false;

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email'),
  groups: z.array(z.string()).min(1, 'At least one role is required'),
  clientId: z.string().min(1, 'Organization is required'),
  employerBpIds: z.array(z.string()).min(1, 'At least one employer is required'),
  sapUserId: z.string().optional(),
}).superRefine((data, ctx) => {
  if (hasDriverRole(data.groups) && !data.sapUserId?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['sapUserId'],
      message: 'SAP User ID is required for driver role',
    });
  }
});

type Schema = z.output<typeof schema>;

const state = reactive<Partial<Schema>>({
  firstName: '',
  lastName: '',
  email: '',
  groups: [],
  clientId: '',
  employerBpIds: [],
  sapUserId: '',
});

const emit = defineEmits<{
  (e: 'searchOrganizations', term: string): void
  (e: 'userCreated'): void
}>();

const working = ref(false);

const selectedEmployer = ref('');
const employerSearchTerm = ref('');

const employerFuse = computed(() =>
  new Fuse(props.employers ?? [], {
    threshold: 0.3,
    ignoreLocation: true,
    keys: ['name', 'bpId'],
  })
);

const filteredEmployers = computed(() => {
  const selected = (props.employers ?? []).find(e => e.bpId === selectedEmployer.value);
  let items: EmployerPartnerDto[];
  
  if (!employerSearchTerm.value) {
    items = (props.employers ?? []).slice(0, 50);
  } else {
    items = employerFuse.value.search(employerSearchTerm.value, { limit: 50 }).map(r => r.item);
  }
  
  if (selected && !items.some(i => i.bpId === selected.bpId)) {
    items = [selected, ...items];
  }

  return items.map(e => ({
    ...e,
    label: `${e.name} (SAP: ${e.bpId})`,
  }));
});

const roleDisplayName = (path: string) => {
  const segment = path.split('/').pop() ?? path;
  
  return segment.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const groupItems = computed(() =>
  (props.groups ?? []).map(g => ({
    ...g,
    label: roleDisplayName(g.path),
  }))
);

const singleSupplierRole = computed(() => {
  if (!props.isSupplierSuperUser || props.supplierRoles.length !== 1) {
    return null;
  }
  
  return props.groups?.find(g => g.path.endsWith(props.supplierRoles[0])) ?? null;
});

const showRoleDropdown = computed(() =>
  !props.isSupplierSuperUser || props.supplierRoles.length >= 2
);

const isDriverSelected = computed(() => hasDriverRole(state.groups));

watch(selectedEmployer, (val) => {
  state.employerBpIds = val ? [val] : [];
});

watch(() => props.employers, (emps) => {
  if (emps?.length === 1) {
    selectedEmployer.value = emps[0].bpId;
  }
});

watch(() => props.supplierRoles, (roles) => {
  if (props.isSupplierSuperUser && roles.length === 1) {
    const matchingGroup = props.groups?.find(g => g.path.endsWith(roles[0]));
    
    if (matchingGroup) {
      state.groups = [matchingGroup.path];
    }
  }
}, { immediate: true });

function resetForm() {
  state.firstName = '';
  state.lastName = '';
  state.email = '';
  state.groups = [];
  state.clientId = '';
  selectedEmployer.value = '';
  state.employerBpIds = [];
  state.sapUserId = '';
}

async function onSubmit(event: FormSubmitEvent<Schema>) {
  try {
    working.value = true;
    
    const result = await apiAdapter.invokeRaw(
      (cfg) => api.userManagement({ ...state } as CreateUserDto, cfg),
      (err) => {
        switch (err.status) {
          case 403:
            showError('You are not authorized to create this user');
            break;
          case 409:
            showError('User already exists');
            break;
          case 500:
            showError('Server error creating user');
            break;
          default:
            showError('Unknown error creating user');
            break;
        }
      }
    );
    
    if (result.type === 'success') {
      toast.add({
        title: 'User created',
        description: `${event.data.firstName} ${event.data.lastName} created. They will receive an email to verify their account and set their password.`,
        color: 'success',
        icon: 'i-lucide-check',
      });
      
      resetForm();
      
      emit('userCreated');
    }
  } catch {
    showError('Failed to save user');
  } finally {
    working.value = false;
  }
}
</script>